import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { localPrintBridge, type CommandPrintJob } from '@/lib/printing/localBridge';
import { adminPwaCanInstall, installAdminPwa } from '@/lib/pwa/install';

const db = supabase as any;
const MULTIFOODS_COUNTER_BUSINESS_ID = 'd014a201-ec56-41a5-8ffc-015348a4dcce';

interface TopbarProps {
  sidebarCollapsed: boolean;
}

export default function Topbar({ sidebarCollapsed }: TopbarProps) {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<Array<{ id: string; orderNumber: string; customerName: string }>>([]);
  const [unreadOrderIds, setUnreadOrderIds] = useState<string[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [canInstallApp, setCanInstallApp] = useState(() => adminPwaCanInstall());
  const processingJobs = useRef(new Set<string>());
  const reviewedOrdersRef = useRef(new Set<string>());
  const notificationAudio = useRef<HTMLAudioElement | null>(null);
  const shouldAlert = useRef(false);
  const alertLoop = useRef<number | null>(null);

  useEffect(() => {
    const refreshInstallState = () => setCanInstallApp(adminPwaCanInstall());
    window.addEventListener('fooddash:install-ready', refreshInstallState);
    window.addEventListener('fooddash:install-finished', refreshInstallState);
    refreshInstallState();
    return () => {
      window.removeEventListener('fooddash:install-ready', refreshInstallState);
      window.removeEventListener('fooddash:install-finished', refreshInstallState);
    };
  }, []);

  const stopAlertLoop = () => {
    shouldAlert.current = false;
    if (alertLoop.current !== null) window.clearInterval(alertLoop.current);
    alertLoop.current = null;
    if (!notificationAudio.current) return;
    notificationAudio.current.pause();
    notificationAudio.current.currentTime = 0;
  };

  const markOrdersReviewed = (orderIds: string[]) => {
    if (!user?.businessId || !orderIds.length) return;
    const storageKey = `fooddash:reviewed-web-orders:${user.businessId}:${user.id}`;
    const reviewed = reviewedOrdersRef.current;
    orderIds.forEach((id) => reviewed.add(id));
    localStorage.setItem(storageKey, JSON.stringify([...reviewed].slice(-100)));
    setUnreadOrderIds((current) => current.filter((id) => !reviewed.has(id)));
  };

  useEffect(() => {
    if (!user?.businessId) return;
    const reviewedStorageKey = `fooddash:reviewed-web-orders:${user.businessId}:${user.id}`;
    const reviewedOrders = new Set<string>(JSON.parse(localStorage.getItem(reviewedStorageKey) || '[]') as string[]);
    reviewedOrdersRef.current = reviewedOrders;
    notificationAudio.current ??= new Audio('/audio/fooddash-notification.mp3');
    notificationAudio.current.loop = false;
    notificationAudio.current.preload = 'auto';
    const unlockAudio = () => {
      if (shouldAlert.current) void notificationAudio.current?.play().catch(() => undefined);
    };
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    const playAlert = () => {
      shouldAlert.current = true;
      if (notificationAudio.current) notificationAudio.current.currentTime = 0;
      void notificationAudio.current?.play().catch(() => undefined);
    };
    const startAlertLoop = () => {
      playAlert();
      if (alertLoop.current === null) alertLoop.current = window.setInterval(playAlert, 4000);
    };
    const getOpenShiftStart = async () => {
      if (!user.id) return null;
      if (user.isB2B) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        return start.toISOString();
      }
      const { data } = await db.from('cash_sessions').select('opened_at').eq('business_id', user.businessId).eq('user_id', user.id).is('closed_at', null).maybeSingle();
      return typeof data?.opened_at === 'string' ? data.opened_at : null;
    };
    const processPrintJob = async (job: { id: string; payload?: unknown; status?: string; attempts?: number; created_at?: string }) => {
      if (job.status !== 'pending' && job.status !== 'failed') return;
      if (processingJobs.current.has(job.id)) return;
      if (!await getOpenShiftStart()) return;
      processingJobs.current.add(job.id);
      try {
        await localPrintBridge.health();
        const [{ data: printer }, { data: claimed }] = await Promise.all([
          supabase.from('printer_devices').select('name,connection_type,ip_address,port,configuration').eq('business_id', user.businessId!).order('created_at').limit(1).maybeSingle(),
          db.from('print_jobs').update({ status: 'processing', processing_started_at: new Date().toISOString(), attempts: (job.attempts ?? 0) + 1 }).eq('id', job.id).in('status', ['pending', 'failed']).select('id').maybeSingle(),
        ]);
        if (!claimed) return;
        const configuration = printer?.configuration as Record<string, unknown> | null;
        const isUsb = printer?.connection_type === 'usb';
        const printerName = typeof configuration?.windowsPrinterName === 'string'
          ? configuration.windowsPrinterName
          : isUsb && typeof printer?.name === 'string' ? printer.name : undefined;
        const printerIp = typeof printer?.ip_address === 'string' ? printer.ip_address : undefined;
        if (!isUsb && !printerName && !printerIp) throw new Error('No hay una impresora local configurada para este negocio.');
        const requestedCopies = typeof (job.payload as Record<string, unknown> | null)?.copies === 'number' ? Number((job.payload as Record<string, unknown>).copies) : 2;
        await localPrintBridge.print({
          ...(job.payload as Omit<CommandPrintJob, 'printerName'>),
          createdAt: job.created_at ? new Date(job.created_at).toLocaleString('es-MX') : String((job.payload as Record<string, unknown> | null)?.createdAt ?? ''),
          printerName,
          printerIp,
          printerPort: typeof printer?.port === 'number' ? printer.port : 9100,
          contentWidthMm: configuration?.contentWidthMm === 58 ? 58 : 80,
          copies: Math.max(2, requestedCopies),
        });
        await db.from('print_jobs').update({ status: 'printed', processing_started_at: null, printed_at: new Date().toISOString(), last_error: null }).eq('id', job.id);
      } catch (reason) {
        await db.from('print_jobs').update({ status: 'failed', processing_started_at: null, last_error: reason instanceof Error ? reason.message : 'El puente local no respondió.' }).eq('id', job.id).neq('status', 'printed');
      } finally { processingJobs.current.delete(job.id); }
    };

    const recoverPrintJobs = async () => {
      if (!await getOpenShiftStart()) return;
      const { data } = await db.from('print_jobs').select('id,payload,status,attempts,created_at').eq('business_id', user.businessId).in('status', ['pending', 'failed']).lt('attempts', 100).order('created_at').limit(20) as { data: Array<{ id: string; payload?: unknown; status?: string; attempts?: number; created_at?: string }> | null };
      for (const job of data ?? []) void processPrintJob(job);
    };
    const recoverInterruptedJobs = async () => {
      if (!await getOpenShiftStart()) return;
      // El puente confirma hasta que terminó de transmitir todas las copias.
      // No recicles un trabajo lento mientras la térmica sigue imprimiéndolo.
      const staleBefore = new Date(Date.now() - 180_000).toISOString();
      await db.from('print_jobs').update({ status: 'failed', processing_started_at: null, last_error: 'Impresión interrumpida; se reintentará automáticamente.' })
        .eq('business_id', user.businessId).eq('status', 'processing').is('printed_at', null)
        .or(`processing_started_at.is.null,processing_started_at.lt.${staleBefore}`);
      recoverPrintJobs();
    };
    const clearExternalOrders = () => {
      stopAlertLoop();
      setIncoming([]);
      setUnreadOrderIds([]);
    };
    const recoverWebOrders = async () => {
      const notificationStartAt = await getOpenShiftStart();
      if (!notificationStartAt) { clearExternalOrders(); return; }
      let query = db.from('orders').select('id,order_number,customer_name,channel,taken_by,created_at').eq('business_id', user.businessId).gte('created_at', notificationStartAt).order('created_at', { ascending: false }).limit(20);
      if (!user.isB2B) query = query.eq('channel', 'web');
      const { data } = await query as { data: Array<{ id: string; order_number: string; customer_name: string; channel: string; taken_by: string }> | null };
      const isMultifoodsCounter = user.businessId === MULTIFOODS_COUNTER_BUSINESS_ID;
      const external = (data ?? []).filter((order) => order.channel === 'web' || (!isMultifoodsCounter && order.taken_by !== user.id));
      const recent = external.map((order) => ({ id: order.id, orderNumber: order.order_number, customerName: order.customer_name || 'Cliente' }));
      const unseen = recent.filter((order) => !reviewedOrders.has(order.id));
      setIncoming(recent);
      setUnreadOrderIds(unseen.map((order) => order.id));
      if (unseen.length) startAlertLoop();
      window.dispatchEvent(new Event('fooddash:orders-changed'));
    };
    void recoverInterruptedJobs();
    recoverWebOrders();
    const recoveryInterval = window.setInterval(() => { recoverPrintJobs(); recoverWebOrders(); }, 7000);

    const channel = supabase.channel(`incoming-orders-${user.businessId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'orders', filter: `business_id=eq.${user.businessId}` }, async (event) => {
        const order = event.new as { id: string; order_number?: string; customer_name?: string; channel?: string; taken_by?: string };
        if (order.channel !== 'web' && (order.taken_by === user.id || user.businessId === MULTIFOODS_COUNTER_BUSINESS_ID)) return;
        if (!await getOpenShiftStart()) return;
        setIncoming((current) => [{ id: order.id, orderNumber: order.order_number || (user.isB2B ? 'Pedido de ruta' : 'Pedido web'), customerName: order.customer_name || 'Cliente' }, ...current].slice(0, 20));
        setUnreadOrderIds((current) => current.includes(order.id) ? current : [order.id, ...current]);
        startAlertLoop();
        window.dispatchEvent(new Event('fooddash:orders-changed'));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'print_jobs', filter: `business_id=eq.${user.businessId}` }, async (event) => {
        if (!await getOpenShiftStart()) return;
        await processPrintJob(event.new as { id: string; payload?: unknown; status?: string; attempts?: number; created_at?: string });
      }).subscribe();
    const onVisible = () => { if (document.visibilityState === 'visible') { recoverPrintJobs(); recoverWebOrders(); } };
    const onCashSessionChanged = () => { void recoverPrintJobs(); void recoverWebOrders(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('fooddash:cash-session-changed', onCashSessionChanged);
    return () => {
      window.clearInterval(recoveryInterval);
      stopAlertLoop();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('fooddash:cash-session-changed', onCashSessionChanged);
      document.removeEventListener('visibilitychange', onVisible);
      void supabase.removeChannel(channel);
      notificationAudio.current = null;
    };
  }, [user?.businessId, user?.id, user?.isB2B, user?.role]);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/kitchen') return 'Modo Cocina';
    if (path.startsWith('/orders/capture')) return user?.isB2B ? 'Nueva venta' : 'Tomar Pedido';
    if (path.startsWith('/orders/history')) return user?.isB2B ? 'Ventas y caja' : 'Historial de Pedidos';
    if (path === '/customers') return user?.isB2B ? 'Clientes empresariales' : 'Clientes';
    if (path === '/products') return user?.isB2B ? 'Catálogo de productos' : 'Productos';
    if (path === '/inventory') return user?.isB2B && new URLSearchParams(location.search).get('section') === 'purchases' ? 'Compras y gastos' : 'Inventario';
    if (path === '/reports') return 'Reportes';
    if (path === '/business-sales') {
      const section = new URLSearchParams(location.search).get('section');
      return ({ customers: 'Clientes empresariales', catalog: 'Presentaciones y precios', quotes: 'Cotizaciones', scheduled: 'Pedidos programados', credit: 'Crédito y cobranza', delivery: 'Rutas y reparto' } as Record<string, string>)[section || ''] || 'Gestión comercial';
    }
    if (path === '/billing') return 'Facturación';
    if (path === '/settings') return 'Configuración';
    if (path === '/admin') return 'Panel Super Administrador';
    return 'FoodDash';
  };

  const roleLabel = () => {
    if (!user) return '';
    switch (user.role) {
      case 'super_admin':
        return 'Super Admin';
      case 'business_admin':
        return 'Administrador';
      case 'supervisor':
        return 'Supervisor';
      case 'employee':
        return 'Vendedor';
    }
  };

  const roleBadgeColor = () => {
    if (!user) return 'bg-foreground-200 text-foreground-700';
    switch (user.role) {
      case 'super_admin':
        return 'bg-accent-100 text-accent-700';
      case 'business_admin':
        return 'bg-primary-100 text-primary-700';
      case 'supervisor':
        return 'bg-blue-100 text-blue-700';
      case 'employee':
        return 'bg-secondary-100 text-secondary-700';
    }
  };

  return (
    <header
      className="fixed top-0 right-0 z-30 h-16 bg-background-50/80 backdrop-blur-md border-b border-background-200/70 transition-all duration-300"
      style={{ left: sidebarCollapsed ? '72px' : '260px' }}
    >
      <div className="h-full px-4 md:px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-foreground-900 font-heading">
            {getPageTitle()}
          </h2>
          {!user?.isB2B && <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-background-100 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
            <span className="text-xs text-foreground-600 whitespace-nowrap">
              {user?.businessName || 'FoodDash'}
            </span>
          </div>}
          {user && !user.isB2B && (
            <span className={`hidden sm:inline-block text-xs font-semibold rounded-full px-2 py-0.5 ${roleBadgeColor()} whitespace-nowrap`}>
              {roleLabel()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {canInstallApp && <button onClick={() => void installAdminPwa().then(() => setCanInstallApp(adminPwaCanInstall()))} className="hidden h-9 items-center gap-2 rounded-lg bg-primary-600 px-3 text-xs font-bold text-white shadow-sm hover:bg-primary-700 sm:flex" title="Instalar FoodDash como aplicación"><i className="ri-download-cloud-2-line text-base" />Instalar app</button>}
          <button
            onClick={toggleTheme}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            title={theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}
          >
            <i className={`text-lg ${theme === 'light' ? 'ri-moon-line' : 'ri-sun-line'}`}></i>
          </button>

          <button onClick={() => { stopAlertLoop(); if (unreadOrderIds.length) markOrdersReviewed(unreadOrderIds); setNotificationsOpen((value) => !value); }} className="w-10 h-10 rounded-lg flex items-center justify-center text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer relative" title="Pedidos y notificaciones">
            <i className="ri-notification-3-line text-lg"></i>
            {unreadOrderIds.length > 0 && <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unreadOrderIds.length}</span>}
          </button>
          {notificationsOpen && <div className="absolute right-4 top-14 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-background-200 bg-background-50 p-2 shadow-xl"><div className="flex items-center justify-between px-3 py-2"><b className="text-sm">{user?.isB2B ? 'Pedidos recibidos' : 'Pedidos externos'}</b>{unreadOrderIds.length > 0 && <button onClick={() => markOrdersReviewed(unreadOrderIds)} className="text-xs text-primary-600">Marcar revisados</button>}</div>{incoming.length === 0 ? <p className="px-3 py-5 text-center text-xs text-foreground-500">No hay pedidos externos recientes.</p> : <div className="max-h-[60vh] space-y-1 overflow-y-auto">{incoming.map((item) => { const unread = unreadOrderIds.includes(item.id); return <button key={item.id} type="button" onClick={() => { markOrdersReviewed([item.id]); stopAlertLoop(); setNotificationsOpen(false); navigate('/orders/history', { state: { orderNumber: item.orderNumber } }); }} className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-background-200 transition-colors ${unread ? 'bg-primary-50' : 'bg-background-100'}`}><span className="flex items-center gap-2"><b>{item.orderNumber}</b>{unread && <span className="h-2 w-2 rounded-full bg-red-500" />}</span><span className="block text-xs text-foreground-500">{item.customerName}</span><span className="mt-1 block text-xs font-semibold text-primary-600">Ver detalle del pedido</span></button>; })}</div>}</div>}

        </div>
      </div>
    </header>
  );
}
